import { FormEvent, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import {
  useStripe,
  useElements,
  PaymentElement,
  LinkAuthenticationElement,
} from '@stripe/react-stripe-js'
import {
  StripeLinkAuthenticationElementChangeEvent,
  StripePaymentElementOptions,
} from '@stripe/stripe-js'

import { TGetItemFn } from '@/config/types'
import { PageName } from '@/enums/pageName'
import { PaymentMethodService } from '@/enums/paymentMethodServices'
import { PaymentMethod, PaymentPlatform } from '@/enums/payments'
import { PaymentScreenVersion } from '@/enums/paymentScreenVersion'
import { prepareContactStateRoutesMap } from '@/utils/paymentScreenUtils'
import { getPath } from '@/utils/urlUtils'

import { PrimaryButtonWithIcon } from '@/components/buttons/primaryButton'

import { ApiService } from '@/api'
import { TContactAccessState, TSubscribeStripeV2Payload } from '@/api/types'
import { emailKey } from '@/auth/types'
import { useAuth } from '@/auth/useAuth'
import { resetContactStateCheckTimeout } from '@/commonPages/paymentScreenPage/hooks/contactState/useCheckContactState'
import { contactNameKey } from '@/core/constants'
import { ISessionContext } from '@/core/session'
import { useSession } from '@/core/session/useSession'
import { trackPaymentInfoAdded, trackPaymentClick, trackPurchaseStarted } from '@/events/events'

import { TStripeCheckoutFormProps } from './types'
import { InputErrorMessage } from '../inputErrorMessage'
import { PaymentMethodIcons } from '../paymentMethodIcons'

const options: StripePaymentElementOptions = {
  fields: {
    billingDetails: {
      email: 'auto',
      address: { country: 'never' },
    },
  },
  terms: { card: 'never' },
  wallets: {
    applePay: 'never',
    googlePay: 'never',
  },
}

const getResultUrl = (paymentResultUrl: string, successPage: PageName): URL => {
  const fail = PageName.paymentFailed
  const url = new URL(paymentResultUrl)
  url.searchParams.append('success_page', successPage)
  url.searchParams.append('fail_page', fail)
  url.searchParams.append('payment_method', PaymentMethod.CARD)

  return url
}

function prepareSubscribePayload(
  getItem: TGetItemFn,
  email: string,
  sessionContext: ISessionContext,
  config: IConfig,
  internalPlanId: string
): TSubscribeStripeV2Payload {
  const contactName = getItem(contactNameKey)
  const sessionId = sessionContext.sessionId
  const utmParams = sessionContext.utmParams
  const fbParams = sessionContext.fbParams || {}
  const gaParams = sessionContext.gaParams || {}
  const firstVisitAt = sessionContext.fva
  const firstVisitAtDiff: number = +new Date() - +new Date(firstVisitAt)

  return {
    session_id: sessionId,
    page_set_id: config.PAGE_SET_ID,
    email: email,
    utm_params: {
      ...utmParams,
      ...fbParams,
      ...gaParams,
    },
    first_visit_at_diff: firstVisitAtDiff,
    name: contactName,
    plan_id: internalPlanId,
  }
}

export const StripeCheckoutFormV2 = ({
  pageName,
  planDetails,
  getItem,
  setItem,
  config,
  formConfig,
  successRedirectTo,
  contactStateRoutesMap,
}: TStripeCheckoutFormProps) => {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const sessionContext = useSession()
  const { t } = useTranslation()

  const stripe = useStripe()
  const elements = useElements()

  const [isFormLoading, setIsFormLoading] = useState<boolean>(true) // will be false when stripe is ready
  const [isLinkAuthElLoading, setIsLinkAuthElLoading] = useState<boolean>(true) // will be false when stripe is ready

  const isLoading = isFormLoading || isLinkAuthElLoading

  const [email, setEmail] = useState<string>(() => getItem(emailKey) || '')
  const [requestError, setRequestError] = useState<string>()

  const paymentMethodsOrder = formConfig?.paymentMethodsSection?.order || [
    PaymentMethodService.VISA_BW,
    PaymentMethodService.MASTERCARD_BW,
    PaymentMethodService.MAESTRO_BW,
    PaymentMethodService.DISCOVER_BW,
  ]

  const showPaymentMethodsIcons = PaymentScreenVersion.default === config.PAYMENT_SCREEN_VERSION

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    e.stopPropagation()

    if (!stripe || !elements || !email) {
      // Stripe.js hasn't yet loaded.
      return
    }
    resetContactStateCheckTimeout(setItem)

    setIsFormLoading(true)

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit()
    if (submitError) {
      setIsFormLoading(false)
      return
    }

    setItem('paymentMethod', PaymentMethod.CARD)
    setItem('paymentPlatform', PaymentPlatform.STRIPE)

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const promisesList: Promise<any>[] = [
      trackPurchaseStarted(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        user
      ),
    ]

    setItem(PageName.paymentScreen, '')

    // TODO: is stage variation ok?
    promisesList.push(
      trackPaymentInfoAdded(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user,
        [],
        ['card']
      )
    )
    promisesList.push(
      trackPaymentClick(
        pageName,
        config.PARTNER_ID,
        config.APP_ID,
        config.PAGE_SET_ID,
        planDetails.internalPlanId,
        PaymentMethod.CARD,
        PaymentPlatform.STRIPE,
        sessionContext,
        config.PIXEL_ID,
        config.TWITTER_CONF?.pixel_id,
        config.TIKTOK_PIXEL_ID,
        user
      )
    )

    const redirectToResultPage = async (
      paymentResultUrl: string,
      type: string,
      clientSecret: string
    ) => {
      const redirectTo = getResultUrl(paymentResultUrl, successRedirectTo)
      if (type === 'setup') {
        const intent = await stripe.retrieveSetupIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('setup_intent', intent.setupIntent.id)
      } else {
        const intent = await stripe.retrievePaymentIntent(clientSecret)
        if (intent.error) {
          navigate(getPath(PageName.paymentFailed))
          return
        }
        redirectTo.searchParams.append('payment_intent', intent.paymentIntent.id)
      }
      Promise.all(promisesList).finally(() => {
        setTimeout(function () {
          document.location.href = redirectTo.href
        }, 100)
      })
    }

    const payload = prepareSubscribePayload(
      getItem,
      email,
      sessionContext,
      config,
      planDetails.internalPlanId
    )

    let response
    try {
      response = await ApiService.subscribeStripeV2(payload)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.log('subscribe error', error)

      await logout()

      if (error && error.response && error.response.status === 400) {
        setItem(emailKey, '')

        if (error.response.data?.email === 'Invalid email') {
          setRequestError(
            t('registrationPage.errorMessages.invalidEmail', '') || 'Please, enter a valid email'
          )

          setIsFormLoading(false)
          return
        }
      } else {
        setItem(emailKey, error.response?.data?.email ? error.response.data.email : '')
      }

      if (error.response?.status === 409) {
        const state = error.response?.data?.state
        if (state) {
          const routesMap = prepareContactStateRoutesMap(contactStateRoutesMap || {})
          const path = routesMap[state as TContactAccessState]
          if (path) {
            navigate(path)
            return
          }
        }
      }
      navigate(getPath(PageName.paymentFailed))
      return
    }

    const {
      type,
      client_secret: clientSecret,
      country,
      payment_result_url: paymentResultUrl,
      email: responseEmail,
    } = response.data

    setItem(emailKey, responseEmail)

    const confirmSubscription = type === 'setup' ? stripe.confirmSetup : stripe.confirmPayment

    try {
      const { error } = await confirmSubscription({
        elements,
        clientSecret,
        confirmParams: {
          return_url: getResultUrl(paymentResultUrl, successRedirectTo).href,
          payment_method_data: {
            billing_details: {
              address: {
                country: country?.toUpperCase() || 'US',
              },
            },
          },
        },
      })

      if (error) {
        // This point is only reached if there's an immediate error when confirming the Intent.
        // Show the error to your customer (for example, "payment details incomplete").
        console.log('error2', error)
        redirectToResultPage(paymentResultUrl, type, clientSecret)
        return
      } else {
        // Your customer is redirected to your `return_url`. For some payment
        // methods like iDEAL, your customer is redirected to an intermediate
        // site first to authorize the payment, then redirected to the `return_url`.
      }
    } catch (e) {
      redirectToResultPage(paymentResultUrl, type, clientSecret)
    }
  }

  function onStripeFormReady() {
    setIsFormLoading(false)
  }

  function onLinkauthElementReady() {
    setIsLinkAuthElLoading(false)
  }

  function onEscape() {
    console.log('escape')
  }

  const onEmailChange = useCallback(
    async function (event: StripeLinkAuthenticationElementChangeEvent) {
      if (event.complete) {
        setEmail(event.value.email)
      } else if (email) {
        setEmail('')
      }
      if (requestError) {
        setRequestError('')
      }
    },
    [setEmail, setRequestError, email, requestError]
  )

  console.log('isLoading', isLoading)
  console.log('disabled', !isLoading && !email)

  return (
    <div className="stripe-checkout-form-container">
      {showPaymentMethodsIcons && <PaymentMethodIcons order={paymentMethodsOrder} />}
      <form onSubmit={onSubmit}>
        <PaymentElement options={options} onReady={onStripeFormReady} />
        <LinkAuthenticationElement
          options={{ defaultValues: { email } }}
          onReady={onLinkauthElementReady}
          onEscape={onEscape}
          onChange={onEmailChange}
        />
        <InputErrorMessage rowNumber={1} message={requestError} />
        <div className="button-container">
          <PrimaryButtonWithIcon
            isLoading={isLoading}
            IconComponent={formConfig?.PaymentButtonIconComponent}
            type="submit"
            style={{ textTransform: 'none' }}
            text={formConfig?.paymentButtonText || 'Continue purchase'}
          />
        </div>
      </form>
    </div>
  )
}
